import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { GlobalOutlined, MailOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText, getPortalAccess, invite, revoke, searchFiles, shareFile, unshare } from "./api";
import { formatDateTime, formatEuro } from "../workReports/format";
import "./customerPortal.css";

const { Paragraph, Text } = Typography;

export const portalStatusTag = (t, status) =>
  ({
    none: { text: t("Not invited"), color: "default" },
    login_only: { text: t("Login without portal invitation"), color: "default" },
    invited: { text: t("Invited"), color: "processing" },
    active: { text: t("Active"), color: "success" },
    revoked: { text: t("Switched off"), color: "error" }
  })[status] || { text: status, color: "default" };

/**
 * The customer portal of one customer, from the customer list: invite (email with a link to set
 * the password), switch off, see the offers they answered (with signature) and the files shared
 * with them.
 */
const PortalAccessModal = ({ client, onChanged }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState(null);

  const load = useCallback(() => {
    getPortalAccess(client._id)
      .then(res => setData(res.data.data))
      .catch(error =>
        setMessage({
          type: "error",
          text: errorText(error, t("Could not load the portal access."))
        })
      );
  }, [client._id, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const run = async (key, call, success) => {
    setBusy(key);
    setMessage(null);
    try {
      const res = await call();
      setData(res.data.data);
      if (success) setMessage({ type: "success", text: success });
      onChanged?.();
    } catch (error) {
      setMessage({ type: "error", text: errorText(error, t("Something went wrong")) });
    } finally {
      setBusy("");
    }
  };

  const findFiles = async search => {
    try {
      const res = await searchFiles(search);
      setFiles(res.data.data);
    } catch {
      setFiles([]);
    }
  };

  const access = data?.access;
  const tag = access ? portalStatusTag(t, access.status) : null;

  return (
    <>
      <Button
        type="link"
        icon={<GlobalOutlined />}
        className="customer-portal-trigger"
        onClick={event => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        {t("Customer portal")}
      </Button>
      <Modal
        open={open}
        title={`${t("Customer portal")} – ${client.company || [client.firstName, client.lastName].filter(Boolean).join(" ")}`}
        onCancel={() => setOpen(false)}
        width={720}
        rootClassName="customer-portal-admin"
        destroyOnClose
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            {t("Close")}
          </Button>,
          access?.status !== "revoked" && access && access.status !== "none" ? (
            <Popconfirm
              key="revoke"
              title={t("Switch the portal off for this customer?")}
              okText={t("Switch off")}
              cancelText={t("Cancel")}
              onConfirm={() =>
                run("revoke", () => revoke(client._id), t("The portal is switched off."))
              }
            >
              <Button danger icon={<StopOutlined />} loading={busy === "revoke"}>
                {t("Switch off")}
              </Button>
            </Popconfirm>
          ) : null,
          <Button
            key="invite"
            type="primary"
            icon={<MailOutlined />}
            loading={busy === "invite"}
            disabled={!client.email}
            onClick={() =>
              run(
                "invite",
                () => invite(client._id),
                t("The invitation was sent to {{email}}.", { email: client.email })
              )
            }
          >
            {access && access.status !== "none"
              ? t("Send invitation again")
              : t("Invite to the portal")}
          </Button>
        ]}
      >
        {message && (
          <Alert
            className="customer-portal-gap"
            type={message.type}
            showIcon
            message={message.text}
          />
        )}
        {!client.email && (
          <Alert
            className="customer-portal-gap"
            type="warning"
            showIcon
            message={t("Please enter the customer's email address first.")}
          />
        )}
        <Paragraph type="secondary">
          {t(
            "In the portal the customer sees their sent offers, invoices, appointments and the documents you handed over, accepts offers with a signature and writes messages."
          )}
        </Paragraph>
        {access && (
          <Descriptions size="small" column={1} bordered className="customer-portal-gap">
            <Descriptions.Item label={t("Status")}>
              <Tag color={tag.color}>{tag.text}</Tag>
            </Descriptions.Item>
            {access.invitedAt && (
              <Descriptions.Item label={t("Invited")}>
                {formatDateTime(access.invitedAt, lng)} · {access.invitedTo}
              </Descriptions.Item>
            )}
            {access.activatedAt && (
              <Descriptions.Item label={t("Password set")}>
                {formatDateTime(access.activatedAt, lng)}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        <Typography.Title level={5}>{t("Answers to offers")}</Typography.Title>
        {data?.decisions?.length ? (
          <List
            className="customer-portal-gap"
            dataSource={data.decisions}
            renderItem={d => (
              <List.Item>
                <div className="customer-portal-decision">
                  <div>
                    <Tag color={d.decision === "accepted" ? "success" : "default"}>
                      {d.decision === "accepted" ? t("Accepted") : t("Declined")}
                    </Tag>
                    <strong>
                      {d.offer?.number} {d.offer?.title}
                    </strong>
                  </div>
                  <Text type="secondary">
                    {[d.name, formatDateTime(d.at, lng), formatEuro(d.grossCents, lng)]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                  {d.reason && <Text>{d.reason}</Text>}
                  {d.image && (
                    <img className="customer-portal-signature" src={d.image} alt={t("Signature")} />
                  )}
                  <Text type="secondary" className="customer-portal-hash">
                    SHA-256 {d.contentHash}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No answers yet.")} />
        )}

        <Typography.Title level={5}>{t("Files shared in the portal")}</Typography.Title>
        <Paragraph type="secondary">
          {t(
            "Signed work reports, acceptance and inspection protocols appear automatically. Site diaries and photo reports appear when you email them to the customer's address. You can also share any stored file here."
          )}
        </Paragraph>
        <Space.Compact className="customer-portal-share customer-portal-gap">
          <Select
            showSearch
            allowClear
            filterOption={false}
            value={fileId}
            placeholder={t("Search a stored file")}
            onSearch={findFiles}
            onFocus={() => findFiles("")}
            onChange={setFileId}
            options={files.map(f => ({ value: f._id, label: f.name }))}
            notFoundContent={t("No files found")}
          />
          <Button
            type="primary"
            disabled={!fileId}
            loading={busy === "share"}
            onClick={() =>
              run(
                "share",
                () => shareFile(client._id, { file: fileId }),
                t("The file is shared.")
              ).then(() => setFileId(null))
            }
          >
            {t("Share")}
          </Button>
        </Space.Compact>
        {data?.shares?.length ? (
          <List
            dataSource={data.shares}
            renderItem={s => (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="link"
                    danger
                    loading={busy === `unshare-${s._id}`}
                    onClick={() => run(`unshare-${s._id}`, () => unshare(client._id, s._id))}
                  >
                    {t("Remove")}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={s.title}
                  description={[
                    s.project?.name,
                    formatDateTime(s.sharedAt, lng),
                    s.via === "email" ? t("sent by email") : ""
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </List.Item>
            )}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default PortalAccessModal;
